// ─── src/MeetupAttendeesView.tsx ──────────────────────────────────────────────
import { useState, useCallback, useEffect, useMemo } from "react";
import { Download, Search, X, Users, CheckCircle, Clock3, UserCheck, QrCode, Pencil } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMeetupEvents, useUpdateMeetupEvent } from "./hooks/useMeetupEvents";
import {
  useMeetupAttendees,
  useDeleteMeetupAttendee,
  useUpdateMeetupAttendeeApproval,
  useCheckInById,
  useUndoCheckIn,
  meetupAttendeeKeys,
} from "./hooks/useMeetupAttendees";
import { CheckInScanner } from "./components/dashboard/CheckInScanner";
import { DoorCodesPanel } from "./components/dashboard/DoorCodesPanel";
import { adminDoorCodesApi } from "./api/doorCodes";
import { EditEventModal } from "./components/dashboard/EditEventModal";
import { MeetupAttendeesTable } from "./components/dashboard/MeetupAttendeesTable";
import { Pagination }    from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard }      from "./components/ui/StatCard";
import type { MeetupAttendee } from "./types/meetupAttendee";
import type { MeetupEvent } from "./types/meetupEvent";

const LIMIT = 20;

function exportCsv(rows: MeetupAttendee[], eventTitle: string) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    "Full Name", "Gender", "Age Group", "WhatsApp", "Email", "Governorate", "City/Area",
    "Current Status", "Skills", "LinkedIn", "Why Attend", "Questions",
    "Seat", "Review", "Checked In At", "RSVP'd At",
  ].join(",");
  const body = rows.map((a) =>
    [
      a.fullName, a.gender, a.ageGroup, a.whatsapp, a.email, a.governorate, a.cityArea,
      a.currentStatus, a.skills.join(" | "), a.linkedInUrl ?? "",
      a.whyAttend, a.questionsForEvent ?? "", a.status, a.approvalStatus ?? "pending",
      a.checkedInAt ?? "", a.createdAt,
    ].map(esc).join(","),
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a2 = document.createElement("a");
  a2.href = url;
  a2.download = `attendees_${eventTitle.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`;
  a2.click();
  URL.revokeObjectURL(url);
}

interface Props {
  initialEventId?: string | null;
}

export default function MeetupAttendeesView({ initialEventId }: Props) {
  const qc = useQueryClient();
  const [eventId, setEventId]       = useState(initialEventId ?? "");
  const [status, setStatus]         = useState("");
  const [approvalStatus, setApprovalStatus] = useState("");
  const [search, setSearch]         = useState("");
  const [attended, setAttended]     = useState("");
  // Event finder: narrows the event picker.
  const [eventQuery, setEventQuery] = useState("");
  const [eventGov, setEventGov]     = useState("");
  const [eventDate, setEventDate]   = useState("");
  const [visualPage, setVisualPage] = useState(1);
  const [showScanner, setShowScanner] = useState(false);
  const [editingEvent, setEditingEvent] = useState<MeetupEvent | null>(null);

  useEffect(() => {
    if (initialEventId) setEventId(initialEventId);
  }, [initialEventId]);

  const { data: eventsData } = useMeetupEvents({ page: 1, limit: 500 });
  const events = useMemo(() => eventsData?.data ?? [], [eventsData]);
  const selectedEvent = events.find((e) => e.id === eventId);
  const eventGovOptions = useMemo(
    () => Array.from(new Set(events.map((e) => e.governorate).filter(Boolean))).sort((a, b) => a.localeCompare(b, "ar")),
    [events],
  );
  const filteredEvents = useMemo(() => {
    const q = eventQuery.trim().toLowerCase();
    return events.filter(
      (e) =>
        (!q || e.title.toLowerCase().includes(q) || (e.hostFullNameAr ?? "").toLowerCase().includes(q)) &&
        (!eventGov || e.governorate === eventGov) &&
        // A multi-day meetup matches every day it runs.
        (!eventDate || (e.eventDate <= eventDate && (e.endDate ?? e.eventDate) >= eventDate)),
    );
  }, [events, eventQuery, eventGov, eventDate]);
  const hasEventFilters = !!(eventQuery || eventGov || eventDate);
  const hasAttendeeFilters = !!(search || status || approvalStatus || attended);

  const { data: allForEvent } = useMeetupAttendees(
    { eventId, page: 1, limit: 2000 },
    !!eventId,
  );
  const allRows = allForEvent?.data ?? [];

  const { data: tableData, isLoading, isFetching, isError } = useMeetupAttendees(
    {
      eventId,
      page: visualPage,
      limit: LIMIT,
      ...(status ? { status } : {}),
      ...(approvalStatus ? { approvalStatus } : {}),
      ...(search ? { search } : {}),
      ...(attended ? { attended } : {}),
    },
    !!eventId,
  );

  const { mutate: deleteAttendee, isPending: isDeleting } = useDeleteMeetupAttendee();
  const { mutate: setApproval, isPending: isApproving } = useUpdateMeetupAttendeeApproval();
  const { mutate: checkIn, isPending: isCheckingIn } = useCheckInById();
  const { mutate: undoCheckIn, isPending: isUndoing } = useUndoCheckIn();
  const { mutateAsync: updateEvent, isPending: isUpdatingEvent } = useUpdateMeetupEvent();

  const pageRows  = tableData?.data ?? [];
  const tableMeta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  const confirmedCount = allRows.filter((a) => a.status === "confirmed").length;
  const waitlistCount  = allRows.filter((a) => a.status === "waitlist").length;
  const pendingReview  = allRows.filter((a) => (a.approvalStatus ?? "pending") === "pending").length;
  const attendedCount  = allRows.filter((a) => !!a.checkedInAt).length;

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: meetupAttendeeKeys.all });
  }, [qc]);

  return (
    <div className="space-y-6">
      {/* Event finder + selector */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="text"
              value={eventQuery}
              onChange={(e) => setEventQuery(e.target.value)}
              placeholder="Find an event by name or host…"
              className="w-full pl-9 pr-8 py-2 text-sm bg-brand-card border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green transition-colors"
            />
            {eventQuery && (
              <button onClick={() => setEventQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>
          <select
            value={eventGov}
            onChange={(e) => setEventGov(e.target.value)}
            className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors"
          >
            <option value="">All Governorates</option>
            {eventGovOptions.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            title="Events running on this date"
            className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors [color-scheme:dark]"
          />
          {hasEventFilters && (
            <button
              onClick={() => { setEventQuery(""); setEventGov(""); setEventDate(""); }}
              className="btn-ghost text-sm flex items-center gap-1.5"
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0 w-full">
            <Users size={16} className="text-brand-muted shrink-0" />
            <select
              value={eventId}
              onChange={(e) => { setEventId(e.target.value); setVisualPage(1); }}
              className="w-full sm:w-auto max-w-full py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors"
            >
              <option value="">
                {hasEventFilters ? `Select an event… (${filteredEvents.length} found)` : "Select an event…"}
              </option>
              {/* Keep the open event listed even when the filters hide it. */}
              {selectedEvent && !filteredEvents.includes(selectedEvent) && (
                <option value={selectedEvent.id}>
                  {selectedEvent.title} — {selectedEvent.eventDate} ({selectedEvent.when})
                </option>
              )}
              {filteredEvents.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title} — {e.eventDate} ({e.when})
                </option>
              ))}
            </select>
          </div>

          {selectedEvent && (
            <button
              type="button"
              onClick={() => setEditingEvent(selectedEvent)}
              className="btn-ghost text-sm inline-flex items-center gap-1.5 shrink-0"
            >
              <Pencil size={14} /> Edit meetup
            </button>
          )}
        </div>
      </div>

      {!eventId ? (
        <div className="card p-10 text-center text-brand-muted">
          Select an event above to view its attendee list.
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard label="Capacity" value={selectedEvent?.capacity ?? 0} icon={Users} accent="bg-brand-green" />
            <StatCard label="Registered" value={confirmedCount} icon={CheckCircle} accent="bg-brand-green" />
            <StatCard label="Waitlisted" value={waitlistCount} icon={Clock3} accent="bg-brand-teal" />
            <StatCard label="Pending Review" value={pendingReview} icon={Clock3} accent="bg-brand-teal" />
            <StatCard label="Attended" value={attendedCount} icon={UserCheck} accent="bg-brand-green" />
          </div>

          {/* Door check-in */}
          <div className="flex items-center justify-between gap-3 card p-4">
            <div>
              <p className="text-white text-sm font-semibold flex items-center gap-2">
                <QrCode size={15} className="text-brand-green" /> Door check-in
              </p>
              <p className="text-brand-muted text-xs mt-0.5">
                Scan attendee QR codes to mark them as attended.
              </p>
            </div>
            <button
              onClick={() => setShowScanner((v) => !v)}
              className={showScanner ? "btn-ghost text-sm shrink-0" : "btn-primary text-sm shrink-0"}
            >
              {showScanner ? "Close scanner" : "Open scanner"}
            </button>
          </div>

          {showScanner && <CheckInScanner eventId={eventId} />}

          <DoorCodesPanel
            queryKey={["door-codes", "meetup", eventId]}
            list={() => adminDoorCodesApi.list("meetup", eventId)}
            create={(label) => adminDoorCodesApi.createForEvent("meetup", eventId, label)}
            revoke={adminDoorCodesApi.revoke}
          />

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setVisualPage(1); }}
                placeholder="Search name, email, WhatsApp…"
                className="w-full pl-9 pr-8 py-2 text-sm bg-brand-card border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green transition-colors"
              />
              {search && (
                <button onClick={() => { setSearch(""); setVisualPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
                  <X size={14} />
                </button>
              )}
            </div>

            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setVisualPage(1); }}
              className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors"
            >
              <option value="">All Seats</option>
              <option value="confirmed">Registered</option>
              <option value="waitlist">Waitlist</option>
            </select>

            <select
              value={approvalStatus}
              onChange={(e) => { setApprovalStatus(e.target.value); setVisualPage(1); }}
              className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors"
            >
              <option value="">All Reviews</option>
              <option value="pending">Pending review</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={attended}
              onChange={(e) => { setAttended(e.target.value); setVisualPage(1); }}
              className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors"
            >
              <option value="">All Attendance</option>
              <option value="yes">Attended</option>
              <option value="no">Not attended</option>
            </select>

            {hasAttendeeFilters && (
              <button
                onClick={() => { setSearch(""); setStatus(""); setApprovalStatus(""); setAttended(""); setVisualPage(1); }}
                className="btn-ghost text-sm flex items-center gap-1.5"
              >
                <X size={14} /> Clear filters
              </button>
            )}

            <div className="flex items-center gap-2 ml-auto">
              <button onClick={handleRefresh} disabled={isFetching} className="btn-ghost text-sm flex items-center gap-1.5">
                {isFetching ? "Refreshing…" : "Refresh"}
              </button>
              <button
                onClick={() => exportCsv(allRows, selectedEvent?.title ?? "event")}
                disabled={allRows.length === 0}
                className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <TableSkeleton />
          ) : isError ? (
            <div className="card p-8 text-center text-red-400">Failed to load attendees. Check your API key or network.</div>
          ) : (
            <>
              <MeetupAttendeesTable
                rows={pageRows}
                onDelete={(id) => deleteAttendee(id)}
                onApproval={(id, accepted) => setApproval({ id, accepted })}
                onUndoCheckIn={(id) => undoCheckIn(id)}
                onCheckIn={(id) => checkIn(id)}
                isBusy={isDeleting || isApproving || isCheckingIn || isUndoing}
              />
              <Pagination
                page={tableMeta.page}
                totalPages={tableMeta.totalPages}
                total={tableMeta.total}
                limit={LIMIT}
                onPageChange={setVisualPage}
                isFetching={isFetching}
              />
            </>
          )}
        </>
      )}

      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSave={async (payload) => {
            await updateEvent({ id: editingEvent.id, payload });
            setEditingEvent(null);
          }}
          saving={isUpdatingEvent}
          subtitle="Admin edit — changes go live immediately on the public page and reflect for the catalyst too."
        />
      )}
    </div>
  );
}
